import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchSurveyDetail } from '../../redux/actions/surveyActions';
import { fetchKitchenSurveyDetail } from '../../redux/actions/kitchenSurveyActions';
import { fetchKitchenDiningSurveyDetail } from '../../redux/actions/kitchenDiningSurveyActions';
import { useLanguage } from "../../context/LanguageContext";
import { useSessionTimer } from '../../hooks/useSessionTimer'; // Import the hook


const SurveySectionsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  useSessionTimer();
  const [sectionStatus, setSectionStatus] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canteenData, setCanteenData] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [surveyType, setSurveyType] = useState(null);
  const [surveyId, setSurveyId] = useState(null);

  // Helper function to normalize survey type
  const normalizeSurveyType = (type) => {
    if (!type) return null;
    const lowerType = type.toLowerCase().trim();
    if (lowerType === 'kitchendining') {
      return 'kitchencumdining';
    }
    return lowerType;
  };

  // Helper function to get display name
  const getSurveyDisplayName = (type) => {
    const normalized = normalizeSurveyType(type);
    switch (normalized) {
      case 'kiosk': return t.global.survey_display_kiosk;
      case 'kitchen': return t.global.survey_display_kitchen;
      case 'kitchencumdining': return t.global.survey_display_kitchencumdining;
      default: return t.global.survey_display_default;
    }
  };

  // Helper function to check if a field is completed
  const isCompletedValue = (value) => {
    if (value === null || value === undefined) return false;
    // Handle boolean values
    if (typeof value === 'boolean') return value === true;
    // Handle number values (1 = completed, 0 = not completed)
    if (typeof value === 'number') return value === 1;
    // Handle string values
    const strValue = String(value).toLowerCase().trim();
    const completedValues = ["1", "yes", "true", "completed", "done", "complete"];
    return completedValues.includes(strValue);
  };

  // Helper function to safely get nested property
  const getNestedValue = (obj, path, defaultValue = null) => {
    if (!obj) return defaultValue;
    const keys = path.split('.');
    let current = obj;
    for (let key of keys) {
      if (current[key] === undefined || current[key] === null) {
        return defaultValue;
      }
      current = current[key];
    }
    return current;
  };

  // Check completion from localStorage completedSections
  const getCompletionFromLocalStorage = (sectionId) => {
    try {
      const completedSections = localStorage.getItem("completedSections");
      if (completedSections) {
        const sections = JSON.parse(completedSections);
        return sections[sectionId] === true || sections[sectionId] === "complete";
      }
    } catch (error) {
      console.error("Error reading completedSections:", error);
    }
    return false;
  };

  // ==================== KIOSK SURVEY SECTIONS ====================
  const isKioskSectionCompleted = (sectionKey, data) => {
    if (!data) return false;
    
    // First check localStorage for manual completion flag
    const localStorageCompleted = getCompletionFromLocalStorage(sectionKey);
    if (localStorageCompleted) {
      console.log(`Section ${sectionKey} completed from localStorage`);
      return true;
    }
    
    switch (sectionKey) {
      case 'a': 
        const genInfoDone = getNestedValue(data, 'gen_data.is_gen_info_survey_done');
        return isCompletedValue(genInfoDone);
      case 'b': 
        // Check API response structure
        const qualiDone1 = getNestedValue(data, 'assessment.is_quali_and_quanti_assessment_survey_done');
        if (isCompletedValue(qualiDone1)) return true;
        
        // Check if data exists in defaultSurvey.sections structure
        const sectionBData = getNestedValue(data, 'defaultSurvey.sections.b');
        if (sectionBData && Object.keys(sectionBData).length > 0) {
          console.log(`Section B has data in defaultSurvey.sections.b`);
          return true;
        }
        
        return false;
      case 'c': 
        const waterDone = getNestedValue(data, 'water_safety.is_water_safety_survey_done');
        return isCompletedValue(waterDone);
      case 'd': 
        const genCleanDone = getNestedValue(data, 'gen_clean.is_gen_clean_survey_done');
        return isCompletedValue(genCleanDone);
      case 'e': 
        const safetyDone = getNestedValue(data, 'safety_arrangement.is_safety_arngmnt_survey_done');
        return isCompletedValue(safetyDone);
      case 'f': 
        const sanitationDone = getNestedValue(data, 'sanitation.is_sanitation_survey_done');
        return isCompletedValue(sanitationDone);
      case 'g': 
        const wasteDone = getNestedValue(data, 'waste_management.is_wst_mngmnt_survey_done');
        return isCompletedValue(wasteDone);
      case 'h': 
        const hygieneDone = getNestedValue(data, 'hygiene.is_hygiene_survey_done');
        return isCompletedValue(hygieneDone);
      case 'i': 
        const foodHygieneDone = getNestedValue(data, 'food_hygiene.is_food_hygine_survey_done');
        return isCompletedValue(foodHygieneDone);
      case 'j': 
        const iecDone = getNestedValue(data, 'iec_monitoring.is_iec_monitoring_survey_done');
        return isCompletedValue(iecDone);
      case 'k': 
        const hasChallenges = data?.others?.challenges && data?.others?.challenges.trim() !== '';
        const hasSuggestions = data?.others?.suggestions && data?.others?.suggestions.trim() !== '';
        return hasChallenges && hasSuggestions;
      default: return false;
    }
  };

  // ==================== KITCHEN SURVEY SECTIONS ====================
  const isKitchenSectionCompleted = (sectionKey, data) => {
    if (!data) return false;
    
    // First check localStorage for manual completion flag
    const localStorageCompleted = getCompletionFromLocalStorage(sectionKey);
    if (localStorageCompleted) return true;
    
    switch (sectionKey) {
      case 'a': 
        const genInfoDone = getNestedValue(data, 'gen_data.is_gen_info_survey_done');
        return isCompletedValue(genInfoDone);
      case 'b': 
        const qualiDone = getNestedValue(data, 'quali_quanti_asment.is_quali_quanti_asment_survey_done');
        return isCompletedValue(qualiDone);
      case 'c': 
        const waterDone = getNestedValue(data, 'water_supply.is_water_supply_survey_done');
        return isCompletedValue(waterDone);
      case 'd': 
        const kitchenSpaceDone = getNestedValue(data, 'kitch_spce.is_kitch_spce_survey_done');
        return isCompletedValue(kitchenSpaceDone);
      case 'e': 
        const safetyCleanDone = getNestedValue(data, 'sfty_cleans.is_sfty_cleanl_survey_done');
        return isCompletedValue(safetyCleanDone);
      case 'f': 
        const sanitationDone = getNestedValue(data, 'sanitation.is_sanitation_survey_done');
        return isCompletedValue(sanitationDone);
      case 'g': 
        const wasteDone = getNestedValue(data, 'wst_mngemnt.is_wst_mngemnt_survey_done');
        return isCompletedValue(wasteDone);
      case 'h': 
        const hygieneDone = getNestedValue(data, 'pers_hygn.is_pers_hygn_survey_done');
        return isCompletedValue(hygieneDone);
      case 'i': 
        const foodHygieneDone = getNestedValue(data, 'food_hygn.is_food_hygn_survey_done');
        return isCompletedValue(foodHygieneDone);
      case 'j': 
        const iecDone = getNestedValue(data, 'iec_mon_J1.is_iec_mon_survey_done');
        return isCompletedValue(iecDone);
      case 'k': 
        const hasChallenges = data?.others?.challenges && data?.others?.challenges.trim() !== '';
        const hasSuggestions = data?.others?.suggestions && data?.others?.suggestions.trim() !== '';
        return hasChallenges && hasSuggestions;
      default: return false;
    }
  };

  // ==================== KITCHEN CUM DINING SURVEY SECTIONS ====================
  const isKitchenCumDiningSectionCompleted = (sectionKey, data) => {
    if (!data) return false;
    
    // First check localStorage for manual completion flag
    const localStorageCompleted = getCompletionFromLocalStorage(sectionKey);
    if (localStorageCompleted) return true;
    
    switch (sectionKey) {
      case 'a': 
        const genInfoDone = getNestedValue(data, 'gen_data.is_gen_info_survey_done');
        return isCompletedValue(genInfoDone);
      case 'b': 
        const qualiDone = getNestedValue(data, 'quali_quanti_asment.is_quali_quanti_asment_survey_done');
        return isCompletedValue(qualiDone);
      case 'c': 
        const waterDone = getNestedValue(data, 'water_supply.is_water_supply_survey_done');
        return isCompletedValue(waterDone);
      case 'd': 
        const kitchenSpaceDone = getNestedValue(data, 'kitch_spce.is_kitch_spce_survey_done');
        return isCompletedValue(kitchenSpaceDone);
      case 'e': 
        const safetyCleanDone = getNestedValue(data, 'sfty_cleans.is_sfty_cleanl_survey_done');
        return isCompletedValue(safetyCleanDone);
      case 'f': 
        const sanitationDone = getNestedValue(data, 'sanitation.is_sanitation_survey_done');
        return isCompletedValue(sanitationDone);
      case 'g': 
        const wasteDone = getNestedValue(data, 'wst_mngemnt.is_wst_mngemnt_survey_done');
        return isCompletedValue(wasteDone);
      case 'h': 
        const hygieneDone = getNestedValue(data, 'pers_hygn.is_pers_hygn_survey_done');
        return isCompletedValue(hygieneDone);
      case 'i': 
        const foodHygieneDone = getNestedValue(data, 'food_hygn.is_food_hygn_survey_done');
        return isCompletedValue(foodHygieneDone);
      case 'j': 
        const iecDone = getNestedValue(data, 'iec_mon_J1.is_iec_mon_survey_done');
        return isCompletedValue(iecDone);
      case 'k': 
        const hasChallenges = data?.others?.challenges && data?.others?.challenges.trim() !== '';
        const hasSuggestions = data?.others?.suggestions && data?.others?.suggestions.trim() !== '';
        return hasChallenges && hasSuggestions;
      default: return false;
    }
  };

  // ==================== PARTIAL COMPLETION LOGIC ====================
  const isSectionPartial = (sectionKey, data, type) => {
    if (!data) return false;
    if (sectionKey !== 'k') return false;
    const hasChallenges = data?.others?.challenges && data?.others?.challenges.trim() !== '';
    const hasSuggestions = data?.others?.suggestions && data?.others?.suggestions.trim() !== '';
    return (hasChallenges || hasSuggestions) && !(hasChallenges && hasSuggestions);
  };

  // ==================== MAIN COMPLETION CHECK ====================
  const isSectionCompleted = (sectionKey, data, type) => {
    if (!data) return false;
    const normalizedType = normalizeSurveyType(type);
    let result = false;
    
    switch (normalizedType) {
      case 'kiosk': 
        result = isKioskSectionCompleted(sectionKey, data);
        break;
      case 'kitchen': 
        result = isKitchenSectionCompleted(sectionKey, data);
        break;
      case 'kitchencumdining': 
        result = isKitchenCumDiningSectionCompleted(sectionKey, data);
        break;
      default: 
        result = false;
    }
    
    return result;
  };

  // Function to fetch fresh survey data from API
  const fetchFreshSurveyData = useCallback(async (id, type) => {
    if (!id) return null;
    
    try {
      console.log(`Fetching fresh survey data for ID: ${id}, Type: ${type}`);
      
      let response;
      const normalizedType = normalizeSurveyType(type);
      
      if (normalizedType === 'kiosk') {
        response = await dispatch(fetchSurveyDetail(id));
      } else if (normalizedType === 'kitchen') {
        response = await dispatch(fetchKitchenSurveyDetail(id));
      } else if (normalizedType === 'kitchencumdining') {
        response = await dispatch(fetchKitchenDiningSurveyDetail(id));
      } else {
        console.warn("Unknown survey type:", type);
        return null;
      }
      
      // Extract survey detail from response with multiple possible structures
      let surveyDetail = null;
      
      if (response?.payload?.data) {
        surveyDetail = response.payload.data;
      } else if (response?.data) {
        surveyDetail = response.data;
      } else if (response) {
        surveyDetail = response;
      }
      
      console.log("✅ Fresh survey data loaded:", surveyDetail);
      
      if (!surveyDetail) {
        throw new Error("No survey data received");
      }
      
      // Also preserve any existing defaultSurvey data from localStorage
      const existingSurveyData = localStorage.getItem("surveyData");
      if (existingSurveyData) {
        try {
          const parsedData = JSON.parse(existingSurveyData);
          if (parsedData.defaultSurvey) {
            surveyDetail.defaultSurvey = parsedData.defaultSurvey;
            console.log("Preserved defaultSurvey data from localStorage");
          }
        } catch (e) {
          console.error("Error parsing existing survey data:", e);
        }
      }
      
      // Update localStorage with fresh data
      localStorage.setItem("selectedSurvey", JSON.stringify(surveyDetail));
      
      return surveyDetail;
      
    } catch (error) {
      console.error("❌ Error fetching fresh survey data:", error);
      return null;
    }
  }, [dispatch]);

  // Function to load survey data
  const loadSurveyData = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    
    try {
      console.log("Loading survey data, forceRefresh:", forceRefresh);
      
      // Get data from state or localStorage
      let canteen = state?.canteen || {};
      let surveyDetail = state?.surveyDetail || {};
      let surveyIdFromState = state?.surveyId;
      let surveyTypeFromState = state?.surveyType;
      
      // If not in state, try localStorage
      if (!canteen || Object.keys(canteen).length === 0 || !canteen.area_name) {
        const savedCanteen = localStorage.getItem("selectedCanteen");
        if (savedCanteen) {
          try {
            canteen = JSON.parse(savedCanteen);
            console.log("Loaded canteen from localStorage:", canteen);
          } catch (e) {
            console.error("Error parsing saved canteen:", e);
          }
        }
      }
      
      // Get survey ID from state or localStorage
      let finalSurveyId = surveyIdFromState || localStorage.getItem("surveyId");
      
      // Get survey type
      let determinedSurveyType = surveyTypeFromState || 
                                 canteen.surveyType || 
                                 localStorage.getItem("surveyType") ||
                                 getSurveyTypeFromCanteenType(canteen.canteen_type);
      
      const normalizedSurveyType = normalizeSurveyType(determinedSurveyType);
      
      console.log("Survey ID:", finalSurveyId);
      console.log("Survey type:", normalizedSurveyType);
      
      setSurveyId(finalSurveyId);
      setSurveyType(normalizedSurveyType);
      
      // If we have a survey ID and forceRefresh is true OR we don't have survey detail, fetch from API
      let finalSurveyDetail = surveyDetail;
      
      if (finalSurveyId && (forceRefresh || !surveyDetail || Object.keys(surveyDetail).length === 0)) {
        console.log("Fetching fresh survey data from API...");
        const freshData = await fetchFreshSurveyData(finalSurveyId, normalizedSurveyType);
        if (freshData) {
          finalSurveyDetail = freshData;
        }
      } else if (!finalSurveyDetail || Object.keys(finalSurveyDetail).length === 0) {
        // Try to get from localStorage
        const savedSurvey = localStorage.getItem("selectedSurvey");
        if (savedSurvey) {
          try {
            finalSurveyDetail = JSON.parse(savedSurvey);
            console.log("Loaded survey from localStorage:", finalSurveyDetail);
          } catch (e) {
            console.error("Error parsing saved survey:", e);
          }
        }
      }
      
      // Also try to get surveyData from localStorage (which contains defaultSurvey)
      const savedSurveyData = localStorage.getItem("surveyData");
      if (savedSurveyData && !finalSurveyDetail.defaultSurvey) {
        try {
          const parsedSurveyData = JSON.parse(savedSurveyData);
          if (parsedSurveyData.defaultSurvey) {
            finalSurveyDetail = {
              ...finalSurveyDetail,
              defaultSurvey: parsedSurveyData.defaultSurvey
            };
            console.log("Added defaultSurvey from surveyData localStorage");
          }
        } catch (e) {
          console.error("Error parsing surveyData:", e);
        }
      }
      
      if (!canteen || !canteen.area_name) {
        console.error("Missing canteen data");
        alert(t.global.alert_missing_ids || "Canteen information is missing. Please go back.");
        navigate("/survey");
        return;
      }
      
      // Ensure location IDs are properly set
      const enhancedCanteen = {
        ...canteen,
        district_id: canteen.district_id || canteen.districtId,
        municipality_id: canteen.municipality_id || canteen.municipalityId || canteen.ulb_id,
        ward_id: canteen.ward_id || canteen.wardId,
        canteen_type: canteen.canteen_type,
        id: canteen.id,
        area_name: canteen.area_name,
        surveyType: normalizedSurveyType
      };
      
      setCanteenData(enhancedCanteen);
      setSurveyData(finalSurveyDetail);
      
      // Log the data to verify
      console.log("Final survey data structure:", finalSurveyDetail);
      console.log("Default survey sections:", finalSurveyDetail?.defaultSurvey?.sections);
      console.log("Completed sections from localStorage:", localStorage.getItem("completedSections"));
      
      // Calculate section status
      const calculateSectionStatus = () => {
        const status = {};
        const sections = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k'];
        
        sections.forEach(section => {
          const completed = isSectionCompleted(section, finalSurveyDetail, normalizedSurveyType);
          const partial = isSectionPartial(section, finalSurveyDetail, normalizedSurveyType);
          
          if (completed) {
            status[section] = "completed";
          } else if (partial) {
            status[section] = "partial";
          } else {
            status[section] = "pending";
          }
        });
        
        return status;
      };
      
      const status = calculateSectionStatus();
      console.log("Calculated section status:", status);
      setSectionStatus(status);
      
    } catch (error) {
      console.error("Error loading survey data:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [state, navigate, fetchFreshSurveyData, t]);

  // Function to handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadSurveyData(true);
  };

  // Get survey type from canteen type
  const getSurveyTypeFromCanteenType = (type) => {
    switch (parseInt(type)) {
      case 1: return "kiosk";
      case 2: return "kitchen";
      case 3: return "kitchencumdining";
      default: return "";
    }
  };

  // Initial load
  useEffect(() => {
    console.log("SurveySectionsPage mounted");
    loadSurveyData(false);
  }, [loadSurveyData]);

  // Check if we need to reload when coming back from a section
  useEffect(() => {
    // Check if we're coming back from a section (indicated by returnToSections flag)
    if (state?.returnToSections) {
      console.log("Returning from section, reloading fresh data...");
      // Clear the flag to prevent infinite loop
      const newState = { ...state };
      delete newState.returnToSections;
      window.history.replaceState(newState, '');
      // Force refresh data from API
      loadSurveyData(true);
    }
  }, [state, loadSurveyData]);

  const finalSurveyType = surveyType || normalizeSurveyType(getSurveyTypeFromCanteenType(canteenData?.canteen_type));

  const handleSectionClick = (sectionId) => {
    console.log("Navigating to section:", sectionId);
    console.log("Survey type:", finalSurveyType);
    
    const getSectionPath = (type, section) => {
      let normalizedType = type?.toLowerCase();
      if (normalizedType === 'kitchencumdining') {
        normalizedType = 'kitchencumDining';
      }
      return `/survey/${normalizedType}/section-${section}`;
    };
    
    // Pass current data to the section
    navigate(getSectionPath(finalSurveyType, sectionId), {
      state: {
        canteen: {
          ...canteenData,
          district_id: canteenData?.district_id,
          municipality_id: canteenData?.municipality_id,
          ward_id: canteenData?.ward_id,
          canteen_type_id: canteenData?.canteen_type,
          canteen_type: canteenData?.canteen_type,
          surveyType: finalSurveyType
        },
        surveyId: surveyId,
        surveyDetail: surveyData,
        newSurvey: state?.newSurvey,
        surveyType: finalSurveyType,
        // Add flag to indicate we need to refresh when coming back
        returnToSections: true
      }
    });
  };

  if (isLoading || !canteenData) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full"></div>
      </div>
    );
  }

  const sections = [
    { id: "a", name: t.global.section_a_name, icon: "📋" },
    { id: "b", name: t.global.section_b_name, icon: "🍽️" },
    { id: "c", name: t.global.section_c_name, icon: "💧" },
    { id: "d", name: t.global.section_d_name, icon: "🏠" },
    { id: "e", name: t.global.section_e_name, icon: "🚒" },
    { id: "f", name: t.global.section_f_name, icon: "🧹" },
    { id: "g", name: t.global.section_g_name, icon: "🗑️" },
    { id: "h", name: t.global.section_h_name, icon: "🧼" },
    { id: "i", name: t.global.section_i_name, icon: "🥘" },
    { id: "j", name: t.global.section_j_name, icon: "📊" },
    { id: "k", name: t.global.section_k_name, icon: "💡" },
  ];

  const completedCount = Object.values(sectionStatus).filter(s => s === "completed").length;
  const partialCount = Object.values(sectionStatus).filter(s => s === "partial").length;
  const total = sections.length;
  const percentage = Math.round(((completedCount + partialCount * 0.5) / total) * 100);

  const getTypeName = (canteenType) => {
    const type = parseInt(canteenType);
    switch (type) {
      case 1: return t.global.type_kiosk || "Kiosk";
      case 2: return t.global.type_kitchen || "Kitchen";
      case 3: return t.global.type_kitchen_cum_dining || "Kitchen Cum Dining";
      default: return t.global.type_unknown || "Unknown";
    }
  };

  const isResumingSurvey = surveyId || localStorage.getItem("surveyId");

  // Format for completed/partial count display
  const completedText = t.global.completed_count_format?.replace("{completed}", completedCount).replace("{total}", total) || `${completedCount}/${total} Completed`;
  const partialText = partialCount > 0 ? (t.global.partial_count_format?.replace("{partial}", partialCount) || ` (${partialCount} partial)`) : '';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="bg-white p-6 rounded-xl shadow mb-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold">
              {t.global.survey_title}
            </h2>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                isRefreshing 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              <svg 
                className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {isRefreshing ? t.global.refreshing || 'Refreshing...' : t.global.refresh || 'Refresh'}
            </button>
          </div>
          
          <div className="text-center text-gray-600 mb-4">
            <p className="font-semibold text-lg">{canteenData.area_name || t.global.na}</p>
            <p className="text-sm">
              {getTypeName(canteenData.canteen_type)} • {t.global.canteen_id_label} {canteenData.id}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t.global.survey_type_label} {getSurveyDisplayName(finalSurveyType)}
            </p>
            
            {isResumingSurvey && (
              <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">
                  🔄 {t.global.resuming_survey}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {t.global.survey_id_label} {surveyId}
                </p>
              </div>
            )}
          </div>

          {/* Progress */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>
                {completedText}{partialText}
              </span>
              <span>{percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden relative">
              <div
                className="bg-green-500 h-2 absolute left-0 top-0"
                style={{ width: `${(completedCount / total) * 100}%` }}
              />
              <div
                className="bg-amber-400 h-2 absolute left-0 top-0"
                style={{ width: `${((completedCount + partialCount) / total) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Sections Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sections.map((section) => {
            const status = sectionStatus[section.id] || "pending";
            let bg, border, text, icon, statusText;

            if (status === "completed") {
              bg = "bg-green-100";
              border = "border-green-300";
              text = "text-green-700";
              icon = "✓";
              statusText = t.global.status_completed;
            } else if (status === "partial") {
              bg = "bg-amber-100";
              border = "border-amber-300";
              text = "text-amber-700";
              icon = "~";
              statusText = t.global.status_partial;
            } else {
              bg = "bg-blue-100";
              border = "border-blue-300";
              text = "text-blue-700";
              icon = "○";
              statusText = t.global.status_not_started;
            }

            return (
              <button
                key={section.id}
                onClick={() => handleSectionClick(section.id)}
                className={`p-5 rounded-xl border shadow-sm transition transform hover:scale-[1.02] ${bg} ${border}`}
              >
                <div className="flex justify-end mb-2">
                  <span className={`font-bold ${text}`}>{icon}</span>
                </div>
                <div className="text-2xl mb-2">{section.icon}</div>
                <div className="font-medium">{section.name}</div>
                <div className="text-xs mt-2 font-medium">{statusText}</div>
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="mt-8 flex justify-center gap-4">
          <button
            onClick={() => {
              localStorage.removeItem("selectedCanteen");
              localStorage.removeItem("selectedSurvey");
              localStorage.removeItem("surveyType");
              localStorage.removeItem("surveyId");
              navigate("/survey");
            }}
            className="px-6 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            {t.global.back_to_canteen_selection}
          </button>

          <button
            onClick={() => {
              const allCompleted = Object.values(sectionStatus).every(s => s === "completed");
              if (allCompleted) {
                navigate("/survey/summary", {
                  state: { 
                    canteen: canteenData, 
                    surveyDetail: surveyData,
                    surveyId: surveyId,
                    surveyType: finalSurveyType
                  }
                });
              } else {
                const alertMessage = `${t.global.alert_complete_all_sections || "Please complete all sections before submitting."}\n\n${t.global.alert_completed?.replace("{completed}", completedCount).replace("{total}", total) || `Completed: ${completedCount}/${total}`}\n${t.global.alert_partial?.replace("{partial}", partialCount) || `Partial: ${partialCount}`}\n${t.global.alert_remaining?.replace("{remaining}", total - completedCount - partialCount) || `Remaining: ${total - completedCount - partialCount}`}`;
                alert(alertMessage);
              }
            }}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t.global.submit_survey}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SurveySectionsPage;